import React, { createContext, useContext, useReducer, useCallback, useMemo, ReactNode } from 'react';
import type {
  TraceSidebarState,
  ViewMode,
  FilterMode,
  DetailTab,
  ContentFormat,
  DisplaySettings
} from '@/types/trace';

// Initial state
const initialState: TraceSidebarState = {
  viewMode: 'tree',
  filterMode: 'show_all',
  selectedTraceId: null,
  highlightedPath: [],
  expandedNodes: new Set<string>(),
  displaySettings: {
    showLatency: true,
    showTokens: true,
    showCost: false,
    showTags: false,
  },
  waterfallZoom: 1,
  detailPanelOpen: false,
  detailPanelTab: 'run',
  contentFormat: 'yaml',
};

// Action types
type Action =
  | { type: 'SET_VIEW_MODE'; payload: ViewMode }
  | { type: 'SET_FILTER_MODE'; payload: FilterMode }
  | { type: 'SET_SELECTED_TRACE'; payload: string | null }
  | { type: 'SET_HIGHLIGHTED_PATH'; payload: string[] }
  | { type: 'TOGGLE_NODE_EXPANSION'; payload: string }
  | { type: 'EXPAND_NODE'; payload: string }
  | { type: 'COLLAPSE_NODE'; payload: string }
  | { type: 'EXPAND_ALL' }
  | { type: 'COLLAPSE_ALL' }
  | { type: 'SET_DISPLAY_SETTINGS'; payload: Partial<DisplaySettings> }
  | { type: 'SET_WATERFALL_ZOOM'; payload: number }
  | { type: 'SET_DETAIL_PANEL_OPEN'; payload: boolean }
  | { type: 'SET_DETAIL_TAB'; payload: DetailTab }
  | { type: 'SET_CONTENT_FORMAT'; payload: ContentFormat }
  | { type: 'RESET_STATE' };

// Reducer
function reducer(state: TraceSidebarState, action: Action): TraceSidebarState {
  switch (action.type) {
    case 'SET_VIEW_MODE':
      return { ...state, viewMode: action.payload };

    case 'SET_FILTER_MODE':
      return { ...state, filterMode: action.payload };

    case 'SET_SELECTED_TRACE':
      return {
        ...state,
        selectedTraceId: action.payload,
        detailPanelOpen: action.payload !== null,
      };

    case 'SET_HIGHLIGHTED_PATH':
      return { ...state, highlightedPath: action.payload };

    case 'TOGGLE_NODE_EXPANSION': {
      const newExpanded = new Set(state.expandedNodes);
      if (newExpanded.has(action.payload)) {
        newExpanded.delete(action.payload);
      } else {
        newExpanded.add(action.payload);
      }
      return { ...state, expandedNodes: newExpanded };
    }

    case 'EXPAND_NODE': {
      const newExpanded = new Set(state.expandedNodes);
      newExpanded.add(action.payload);
      return { ...state, expandedNodes: newExpanded };
    }

    case 'COLLAPSE_NODE': {
      const newExpanded = new Set(state.expandedNodes);
      newExpanded.delete(action.payload);
      return { ...state, expandedNodes: newExpanded };
    }

    case 'EXPAND_ALL':
      // This will be handled by the component that knows all node IDs
      return state;

    case 'COLLAPSE_ALL':
      return { ...state, expandedNodes: new Set<string>() };

    case 'SET_DISPLAY_SETTINGS':
      return {
        ...state,
        displaySettings: { ...state.displaySettings, ...action.payload }
      };

    case 'SET_WATERFALL_ZOOM':
      return { ...state, waterfallZoom: Math.max(0.1, Math.min(10, action.payload)) };

    case 'SET_DETAIL_PANEL_OPEN':
      return { ...state, detailPanelOpen: action.payload };

    case 'SET_DETAIL_TAB':
      return { ...state, detailPanelTab: action.payload };

    case 'SET_CONTENT_FORMAT':
      return { ...state, contentFormat: action.payload };

    case 'RESET_STATE':
      return initialState;

    default:
      return state;
  }
}

// Context type
interface TraceSidebarContextType {
  state: TraceSidebarState;
  setViewMode: (mode: ViewMode) => void;
  setFilterMode: (mode: FilterMode) => void;
  selectTrace: (id: string | null) => void;
  setHighlightedPath: (path: string[]) => void;
  toggleNodeExpansion: (id: string) => void;
  expandNode: (id: string) => void;
  collapseNode: (id: string) => void;
  expandAll: (nodeIds: string[]) => void;
  collapseAll: () => void;
  setDisplaySettings: (settings: Partial<DisplaySettings>) => void;
  setWaterfallZoom: (zoom: number) => void;
  zoomIn: () => void;
  zoomOut: () => void;
  resetZoom: () => void;
  setDetailPanelOpen: (open: boolean) => void;
  setDetailTab: (tab: DetailTab) => void;
  setContentFormat: (format: ContentFormat) => void;
  isNodeExpanded: (id: string) => boolean;
  isNodeSelected: (id: string) => boolean;
  isNodeHighlighted: (id: string) => boolean;
}

const TraceSidebarContext = createContext<TraceSidebarContextType | null>(null);

// Provider component
interface TraceSidebarProviderProps {
  children: ReactNode;
  initialSelectedId?: string | null;
}

export function TraceSidebarProvider({
  children,
  initialSelectedId = null
}: TraceSidebarProviderProps) {
  const [state, dispatch] = useReducer(reducer, {
    ...initialState,
    selectedTraceId: initialSelectedId,
    detailPanelOpen: initialSelectedId !== null,
  });

  const setViewMode = useCallback((mode: ViewMode) => {
    dispatch({ type: 'SET_VIEW_MODE', payload: mode });
  }, []);

  const setFilterMode = useCallback((mode: FilterMode) => {
    dispatch({ type: 'SET_FILTER_MODE', payload: mode });
  }, []);

  const selectTrace = useCallback((id: string | null) => {
    dispatch({ type: 'SET_SELECTED_TRACE', payload: id });
  }, []);

  const setHighlightedPath = useCallback((path: string[]) => {
    dispatch({ type: 'SET_HIGHLIGHTED_PATH', payload: path });
  }, []);

  const toggleNodeExpansion = useCallback((id: string) => {
    dispatch({ type: 'TOGGLE_NODE_EXPANSION', payload: id });
  }, []);

  const expandNode = useCallback((id: string) => {
    dispatch({ type: 'EXPAND_NODE', payload: id });
  }, []);

  const collapseNode = useCallback((id: string) => {
    dispatch({ type: 'COLLAPSE_NODE', payload: id });
  }, []);

  const expandAll = useCallback((nodeIds: string[]) => {
    nodeIds.forEach(id => {
      dispatch({ type: 'EXPAND_NODE', payload: id });
    });
  }, []);

  const collapseAll = useCallback(() => {
    dispatch({ type: 'COLLAPSE_ALL' });
  }, []);

  const setDisplaySettings = useCallback((settings: Partial<DisplaySettings>) => {
    dispatch({ type: 'SET_DISPLAY_SETTINGS', payload: settings });
  }, []);

  const setWaterfallZoom = useCallback((zoom: number) => {
    dispatch({ type: 'SET_WATERFALL_ZOOM', payload: zoom });
  }, []);

  const zoomIn = useCallback(() => {
    dispatch({ type: 'SET_WATERFALL_ZOOM', payload: state.waterfallZoom * 1.2 });
  }, [state.waterfallZoom]);

  const zoomOut = useCallback(() => {
    dispatch({ type: 'SET_WATERFALL_ZOOM', payload: state.waterfallZoom / 1.2 });
  }, [state.waterfallZoom]);

  const resetZoom = useCallback(() => {
    dispatch({ type: 'SET_WATERFALL_ZOOM', payload: 1 });
  }, []);

  const setDetailPanelOpen = useCallback((open: boolean) => {
    dispatch({ type: 'SET_DETAIL_PANEL_OPEN', payload: open });
  }, []);

  const setDetailTab = useCallback((tab: DetailTab) => {
    dispatch({ type: 'SET_DETAIL_TAB', payload: tab });
  }, []);

  const setContentFormat = useCallback((format: ContentFormat) => {
    dispatch({ type: 'SET_CONTENT_FORMAT', payload: format });
  }, []);

  const isNodeExpanded = useCallback((id: string) => {
    return state.expandedNodes.has(id);
  }, [state.expandedNodes]);

  const isNodeSelected = useCallback((id: string) => {
    return state.selectedTraceId === id;
  }, [state.selectedTraceId]);

  const isNodeHighlighted = useCallback((id: string) => {
    return state.highlightedPath.includes(id);
  }, [state.highlightedPath]);

  const value = useMemo<TraceSidebarContextType>(() => ({
    state,
    setViewMode,
    setFilterMode,
    selectTrace,
    setHighlightedPath,
    toggleNodeExpansion,
    expandNode,
    collapseNode,
    expandAll,
    collapseAll,
    setDisplaySettings,
    setWaterfallZoom,
    zoomIn,
    zoomOut,
    resetZoom,
    setDetailPanelOpen,
    setDetailTab,
    setContentFormat,
    isNodeExpanded,
    isNodeSelected,
    isNodeHighlighted,
  }), [
    state,
    setViewMode,
    setFilterMode,
    selectTrace,
    setHighlightedPath,
    toggleNodeExpansion,
    expandNode,
    collapseNode,
    expandAll,
    collapseAll,
    setDisplaySettings,
    setWaterfallZoom,
    zoomIn,
    zoomOut,
    resetZoom,
    setDetailPanelOpen,
    setDetailTab,
    setContentFormat,
    isNodeExpanded,
    isNodeSelected,
    isNodeHighlighted,
  ]);

  return (
    <TraceSidebarContext.Provider value={value}>
      {children}
    </TraceSidebarContext.Provider>
  );
}

// Hook to use the context
export function useTraceSidebar() {
  const context = useContext(TraceSidebarContext);
  if (!context) {
    throw new Error('useTraceSidebar must be used within a TraceSidebarProvider');
  }
  return context;
}

export default TraceSidebarContext;
